/**
 * useSignalR Hook
 * 
 * React hook for managing SignalR connection and real-time notification updates.
 * Automatically connects/disconnects based on authentication state.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { signalRService } from '../services/signalRService';
import {
  addNotification,
  updateNotificationReadStatus,
  removeNotification,
  markAllAsReadLocal,
  updateUnreadCount,
} from '@/app/store/slices/notificationSlice';
import type { RootState } from '@/app/store';
import store from '@/app/store';
import { API_BASE_URL } from '@/app/constants/apiConstants';
import { tokenManager } from '@/app/services/auth/tokenManager';
import {
  Notification,
  SignalRConnectionState,
  SignalRNotificationReadEvent,
  SignalRNotificationDismissedEvent,
  SignalRAllNotificationsReadEvent,
  SignalRUnreadCountUpdateEvent,
} from '../types/notification.types';

// Import translation messages
import enUSMessages from '@/locales/en-US.json';
import zhCNMessages from '@/locales/zh-CN.json';
import zhTWMessages from '@/locales/zh-TW.json';
import jaJPMessages from '@/locales/ja-JP.json';
import esESMessages from '@/locales/es-ES.json';
import frFRMessages from '@/locales/fr-FR.json';

// Map locale codes to their message objects
const messagesMap: Record<string, any> = {
  'en-US': enUSMessages,
  'zh-CN': zhCNMessages,
  'zh-TW': zhTWMessages,
  'ja-JP': jaJPMessages,
  'es-ES': esESMessages,
  'fr-FR': frFRMessages,
};

/**
 * Get translated text based on current language from Redux store
 * This function reads directly from the store to avoid closure issues
 */
const getTranslationFromStore = (key: string): string => {
  const state = store.getState();
  const lang = state.language.currentLanguage;
  const messages = messagesMap[lang] || messagesMap['en-US'];
  const keys = key.split('.');
  let value: any = messages;
  
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) break;
  }
  
  return value || key;
};

/**
 * SignalR hook configuration
 */
interface UseSignalRConfig {
  hubUrl?: string;
  autoConnect?: boolean;
  showToast?: boolean;
  playSound?: boolean;
}

/**
 * SignalR hook return type
 */
interface UseSignalRReturn {
  connectionState: SignalRConnectionState;
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  dismissNotification: (notificationId: number) => Promise<void>;
}

/**
 * useSignalR Hook
 */
export function useSignalR(config: UseSignalRConfig = {}): UseSignalRReturn {
  const {
    hubUrl = `${API_BASE_URL}/hubs/notification`,
    autoConnect = true,
  } = config;

  const dispatch = useDispatch();
  
  const isInitialized = useRef(false);
  const connectionStateRef = useRef<SignalRConnectionState>('disconnected');
  
  // Get auth state from Redux
  const { isAuthenticated, activeAccountId } = useSelector((state: RootState) => {
    console.log('🔥 [useSignalR] Redux selector called', {
      activeAccountId: state.auth.activeAccountId,
      isAuthenticated: state.auth.isAuthenticated,
      fullAuthState: state.auth
    });
    return state.auth;
  });
  
  // Get notification preferences from Redux
  const preferences = useSelector((state: RootState) => {
    console.log('🔥 [useSignalR] Reading preferences from Redux:', {
      hasPreferences: !!state.userPreferences.preferences,
      preferences: state.userPreferences.preferences,
      notificationSystemEnabled: state.userPreferences.preferences?.notificationSystemEnabled,
      notificationToolEnabled: state.userPreferences.preferences?.notificationToolEnabled,
      notificationAdminEnabled: state.userPreferences.preferences?.notificationAdminEnabled,
      notificationSecurityEnabled: state.userPreferences.preferences?.notificationSecurityEnabled
    });
    return state.userPreferences.preferences;
  });
  const notificationSoundEnabled = useSelector((state: RootState) => 
    state.userPreferences.preferences?.notificationSoundEnabled ?? true
  );
  const notificationSoundVolume = useSelector((state: RootState) => 
    state.userPreferences.preferences?.notificationSoundVolume ?? 80
  );
  const notificationToastEnabled = useSelector((state: RootState) => 
    state.userPreferences.preferences?.notificationToastEnabled ?? true
  );
  
  // Use preferences from Redux instead of config
  const showToast = config.showToast ?? notificationToastEnabled;
  const playSound = config.playSound ?? notificationSoundEnabled;
  
  // Initialize ref with current activeAccountId from Redux
  // This ensures we can detect account switches vs initial mount
  const activeAccountIdRef = useRef<string | null>(activeAccountId);

  /**
   * Sync notification sound service with user preferences
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Dynamically import the notification sound service
    import('../services/notificationSoundService').then(({ notificationSoundService }) => {
      // Update sound enabled status
      notificationSoundService.setSoundEnabled(notificationSoundEnabled);
      
      // Update volume (convert from 0-100 to 0-1)
      const volumeDecimal = notificationSoundVolume / 100;
      notificationSoundService.setVolume(volumeDecimal);
      
      console.log('[useSignalR] Synced notification sound preferences:', {
        soundEnabled: notificationSoundEnabled,
        volume: notificationSoundVolume,
        volumeDecimal
      });
    });
  }, [notificationSoundEnabled, notificationSoundVolume]);

  /**
   * Load preferences if missing (fallback)
   * This ensures preferences are available even if they weren't loaded during login
   */
  useEffect(() => {
    if (isAuthenticated && !preferences) {
      console.log('[useSignalR] Preferences not loaded, attempting to fetch');
      
      // Get user from Redux
      const state = store.getState();
      const user = state.user.user;
      
      if (user?.id) {
        console.log('[useSignalR] Fetching preferences for user:', user.id);
        import('../../../store/slices/userPreferencesSlice').then(({ fetchUserPreferences }) => {
          store.dispatch(fetchUserPreferences(user.id as any) as any)
            .then(() => {
              console.log('[useSignalR] Preferences loaded successfully');
            })
            .catch((error: any) => {
              console.error('[useSignalR] Failed to load preferences:', error);
            });
        });
      } else {
        console.warn('[useSignalR] Cannot load preferences: user ID not available');
      }
    }
  }, [isAuthenticated, preferences, dispatch]);

  /**
   * Debug: Log whenever activeAccountId changes
   */
  useEffect(() => {
    console.log('[useSignalR] DEBUG - activeAccountId changed:', {
      activeAccountId,
      isAuthenticated,
      refValue: activeAccountIdRef.current
    });
  }, [activeAccountId]);

  /**
   * Get access token for SignalR authentication using tokenManager
   * (Centralized token management for consistency)
   */
  const getAccessToken = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const tokens = tokenManager.getTokens();
    return tokens?.accessToken || null;
  }, []);

  /**
   * Handle new notification received
   */
  const handleReceiveNotification = useCallback(
    (notification: Notification) => {
      // Get fresh preferences from Redux store to avoid stale closure
      const currentState = store.getState();
      const currentPreferences = currentState.userPreferences.preferences;
      
      console.log('🔥 [useSignalR] handleReceiveNotification called:', {
        notificationId: notification.id,
        category: notification.notificationCategory,
        categoryType: typeof notification.notificationCategory,
        categoryValue: JSON.stringify(notification.notificationCategory),
        hasPreferences: !!currentPreferences,
        preferencesFromClosure: preferences ? {
          notificationSystemEnabled: preferences.notificationSystemEnabled,
          notificationToolEnabled: preferences.notificationToolEnabled,
          notificationAdminEnabled: preferences.notificationAdminEnabled,
          notificationSecurityEnabled: preferences.notificationSecurityEnabled
        } : null,
        preferencesFromStore: currentPreferences ? {
          notificationSystemEnabled: currentPreferences.notificationSystemEnabled,
          notificationToolEnabled: currentPreferences.notificationToolEnabled,
          notificationAdminEnabled: currentPreferences.notificationAdminEnabled,
          notificationSecurityEnabled: currentPreferences.notificationSecurityEnabled
        } : null
      });
      
      // Import filter utilities
      const { shouldShowNotification, shouldShowToast: shouldShowToastFilter, shouldPlaySound: shouldPlaySoundFilter } = 
        require('../utils/notificationFilter');
      
      // Check if notification should be shown based on preferences
      // Use currentPreferences from store instead of closure preferences
      const shouldShow = shouldShowNotification(notification, currentPreferences);
      console.log('🔥 [useSignalR] Filter decision:', {
        shouldShow,
        notificationId: notification.id,
        category: notification.notificationCategory,
        categoryType: typeof notification.notificationCategory,
        categoryIsUndefined: notification.notificationCategory === undefined,
        categoryIsNull: notification.notificationCategory === null,
        categoryIsEmpty: notification.notificationCategory === ''
      });
      
      if (!shouldShow) {
        console.log('🔥 [useSignalR] Notification BLOCKED by filter:', {
          notificationId: notification.id,
          category: notification.notificationCategory,
          title: notification.notificationTitle
        });
        // Still add to store for history, but don't show toast or play sound
        dispatch(addNotification(notification));
        return;
      }
      
      console.log('🔥 [useSignalR] Notification ALLOWED by filter, will show toast/sound');

      // Add to Redux store
      dispatch(addNotification(notification));

      // Show toast notification if enabled and allowed by filters
      if (showToast && shouldShowToastFilter(notification, currentPreferences)) {
        const viewLabel = getTranslationFromStore('common.notifications.toast.view');
        
        toast(notification.notificationTitle, {
          description: notification.notificationMessage,
          // Don't specify duration here - let it use the default from Toaster component
          // which respects user preferences
          action: {
            label: viewLabel,
            onClick: () => {
              window.dispatchEvent(new CustomEvent('openNotificationCenter'));
            },
          },
          actionButtonStyle: {
            background: 'linear-gradient(to right, hsl(var(--primary)), hsl(var(--accent)))',
            color: 'white',
            fontWeight: '600',
            borderRadius: '9999px',
            padding: '0.625rem 1.5rem',
            fontSize: '0.875rem',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            transition: 'all 0.3s ease',
          },
        });
      }

      // Play sound if enabled and allowed by filters
      if (playSound && shouldPlaySoundFilter(notification, currentPreferences)) {
        window.dispatchEvent(new CustomEvent('playNotificationSound'));
      }
    },
    [dispatch, showToast, playSound, preferences]
  );

  /**
   * Handle notification read event
   */
  const handleNotificationRead = useCallback(
    (event: SignalRNotificationReadEvent) => {
      console.log('[useSignalR] Notification read:', event);
      dispatch(
        updateNotificationReadStatus({
          notificationId: event.notificationId,
          readAt: event.readAt,
        })
      );
    },
    [dispatch]
  );

  /**
   * Handle notification dismissed event
   */
  const handleNotificationDismissed = useCallback(
    (event: SignalRNotificationDismissedEvent) => {
      console.log('[useSignalR] Notification dismissed:', event);
      dispatch(removeNotification(event.notificationId));
    },
    [dispatch]
  );

  /**
   * Handle all notifications read event
   */
  const handleAllNotificationsRead = useCallback(
    (event: SignalRAllNotificationsReadEvent) => {
      console.log('[useSignalR] All notifications read:', event);
      dispatch(markAllAsReadLocal());
    },
    [dispatch]
  );

  /**
   * Handle unread count update event
   */
  const handleUnreadCountUpdate = useCallback(
    (event: SignalRUnreadCountUpdateEvent) => {
      console.log('[useSignalR] Unread count update:', event);
      dispatch(updateUnreadCount(event.unreadCount));
    },
    [dispatch]
  );

  /**
   * Handle connection state changed
   */
  const handleConnectionStateChanged = useCallback((state: SignalRConnectionState) => {
    console.log('[useSignalR] Connection state changed:', state);
    connectionStateRef.current = state;
  }, []);

  /**
   * Handle SignalR error
   */
  const handleError = useCallback((error: Error) => {
    console.error('[useSignalR] Error:', error);
    toast.error('Notification connection error', {
      description: error.message,
    });
  }, []);

  /**
   * Initialize SignalR service (only once)
   */
  useEffect(() => {
    if (!isInitialized.current) {
      signalRService.initialize(
        {
          hubUrl,
          getAccessToken,
          heartbeatIntervalMs: 30000,
        },
        {
          onReceiveNotification: handleReceiveNotification,
          onNotificationRead: handleNotificationRead,
          onNotificationDismissed: handleNotificationDismissed,
          onAllNotificationsRead: handleAllNotificationsRead,
          onUnreadCountUpdate: handleUnreadCountUpdate,
          onConnectionStateChanged: handleConnectionStateChanged,
          onError: handleError,
        }
      );
      isInitialized.current = true;
    }
  }, [
    hubUrl,
    getAccessToken,
    handleReceiveNotification,
    handleNotificationRead,
    handleNotificationDismissed,
    handleAllNotificationsRead,
    handleUnreadCountUpdate,
    handleConnectionStateChanged,
    handleError,
  ]);

  /**
   * Auto-connect when authenticated
   */
  useEffect(() => {
    const token = getAccessToken();
    if (autoConnect && isAuthenticated && token) {
      console.log('[useSignalR] User authenticated, connecting SignalR');
      signalRService.connect().catch((error) => {
        console.error('[useSignalR] Auto-connect failed:', error);
      });
    }
  }, [autoConnect, isAuthenticated]);
  
  /**
   * Disconnect when user logs out
   */
  useEffect(() => {
    // When isAuthenticated becomes false (logout), disconnect SignalR
    if (!isAuthenticated && signalRService.isConnected()) {
      console.log('[useSignalR] User logged out, disconnecting SignalR');
      signalRService.disconnect().catch((error) => {
        console.error('[useSignalR] Logout disconnect failed:', error);
      });
    }
  }, [isAuthenticated]);

  /**
   * Reconnect when active account changes (account switch)
   */
  useEffect(() => {
    console.log('[useSignalR] Account switch effect triggered', {
      currentRef: activeAccountIdRef.current,
      newAccountId: activeAccountId,
      isAuthenticated
    });

    // If no account ID, skip
    if (!activeAccountId || !isAuthenticated) {
      console.log('[useSignalR] No account ID or not authenticated, skipping');
      if (activeAccountId === null) {
        activeAccountIdRef.current = null;
      }
      return;
    }

    // Check if this is an actual account switch (not initial mount)
    const isAccountSwitch = activeAccountIdRef.current !== null && activeAccountIdRef.current !== activeAccountId;
    
    // Update the ref
    const previousAccountId = activeAccountIdRef.current;
    activeAccountIdRef.current = activeAccountId;

    // If this is an account switch, reconnect
    if (isAccountSwitch) {
      console.log('[useSignalR] Account switched, reconnecting SignalR', {
        from: previousAccountId,
        to: activeAccountId
      });

      const reconnect = async () => {
        try {
          // Disconnect old connection
          if (signalRService.isConnected()) {
            console.log('[useSignalR] Disconnecting old account connection');
            try {
              await signalRService.disconnect();
            } catch (disconnectError) {
              // Disconnection errors during account switch are expected and acceptable
              // The connection may already be closed by the server
              console.log('[useSignalR] Disconnect during account switch (connection may already be closed)');
            }
          }

          // Small delay to ensure clean disconnect
          await new Promise(resolve => setTimeout(resolve, 200));

          // Get new token for the switched account
          const token = getAccessToken();
          if (token) {
            console.log('[useSignalR] Connecting with new account token');
            await signalRService.connect();
            console.log('[useSignalR] Successfully reconnected with new account');
            
            // Wait a bit longer to ensure token is properly set in axios interceptor
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Fetch unread count for the new account
            console.log('[useSignalR] Fetching unread count for new account');
            try {
              const { fetchUnreadCount } = await import('@/app/store/thunks/notificationThunks');
              await store.dispatch(fetchUnreadCount());
            } catch (fetchError) {
              // Log but don't fail - unread count will be fetched again later
              console.warn('[useSignalR] Failed to fetch unread count after account switch (will retry):', fetchError);
            }
          } else {
            console.warn('[useSignalR] No token available for new account');
          }
        } catch (error) {
          console.error('[useSignalR] Failed to reconnect after account switch:', error);
        }
      };

      reconnect();
    } else {
      console.log('[useSignalR] Initial mount or same account, no reconnection needed', {
        previousAccountId,
        currentAccountId: activeAccountId
      });
    }
  }, [activeAccountId, isAuthenticated, getAccessToken]);
  
  /**
   * Cleanup on actual component unmount (browser close, navigation away)
   */
  useEffect(() => {
    return () => {
      // This cleanup only runs on component unmount
      if (signalRService.isConnected()) {
        console.log('[useSignalR] Component unmounting, disconnecting SignalR');
        signalRService.disconnect().catch((error) => {
          console.error('[useSignalR] Unmount disconnect failed:', error);
        });
      }
    };
  }, []); // Empty array = only runs on mount/unmount

  /**
   * Connect to SignalR hub
   */
  const connect = useCallback(async () => {
    try {
      await signalRService.connect();
    } catch (error) {
      console.error('[useSignalR] Connect failed:', error);
      throw error;
    }
  }, []);

  /**
   * Disconnect from SignalR hub
   */
  const disconnect = useCallback(async () => {
    try {
      await signalRService.disconnect();
    } catch (error) {
      console.error('[useSignalR] Disconnect failed:', error);
      throw error;
    }
  }, []);

  /**
   * Mark notification as read via SignalR
   */
  const markAsReadViaHub = useCallback(async (notificationId: number) => {
    try {
      await signalRService.markAsRead(notificationId);
    } catch (error) {
      console.error('[useSignalR] Mark as read failed:', error);
      throw error;
    }
  }, []);

  /**
   * Dismiss notification via SignalR
   */
  const dismissNotificationViaHub = useCallback(async (notificationId: number) => {
    try {
      await signalRService.dismissNotification(notificationId);
    } catch (error) {
      console.error('[useSignalR] Dismiss notification failed:', error);
      throw error;
    }
  }, []);

  return {
    connectionState: connectionStateRef.current,
    isConnected: signalRService.isConnected(),
    connect,
    disconnect,
    markAsRead: markAsReadViaHub,
    dismissNotification: dismissNotificationViaHub,
  };
}
